import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Copy, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  // Form data for generating content
  const [formData, setFormData] = useState({
    team_home: '',
    team_away: '',
    league: '',
    stadium: '',
    date: '',
    time: '',
    channels: '',
    home_lineup: '',
    away_lineup: '',
    home_score: '',
    away_score: '',
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
        if (response.data.length > 0) {
          setExpandedTemplate(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const getDayName = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { weekday: 'long' });
  };

  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const generateContent = (template) => {
    let content = template.content;
    const replacements = {
      '{TEAM_HOME}': formData.team_home || '[Time Casa]',
      '{TEAM_AWAY}': formData.team_away || '[Time Visitante]',
      '{LEAGUE}': formData.league || '[Campeonato]',
      '{STADIUM}': formData.stadium || '[Estádio]',
      '{DATE}': formatDateBR(formData.date) || '[Data]',
      '{DAY}': formatDateBR(formData.date).split('/')[0] || '[Dia]',
      '{DAY_NAME}': getDayName(formData.date) || '[Dia da Semana]',
      '{TIME}': formData.time || '[Horário]',
      '{CHANNELS}': formData.channels || '[Canais]',
      '{HOME_LINEUP}': formData.home_lineup || '[Escalação Casa]',
      '{AWAY_LINEUP}': formData.away_lineup || '[Escalação Visitante]',
      '{HOME_SCORE}': formData.home_score || '[Placar Casa]',
      '{AWAY_SCORE}': formData.away_score || '[Placar Visitante]',
    };

    Object.entries(replacements).forEach(([key, value]) => {
      content = content.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return content;
  };

  const copyToClipboard = async (templateId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(templateId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" data-testid="loading-state">
        <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
        <p className="text-zinc-400">Carregando templates...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="templates-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
          <FileText className="w-8 h-8 text-neon-green" />
          Templates SEO
        </h1>
        <p className="text-zinc-400 mt-1">Gere textos otimizados para SEO rapidamente</p>
      </div>

      {/* Form */}
      <div className="bg-card-dark border border-zinc-800 p-4 md:p-6">
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Dados do Jogo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="team_home" className="text-zinc-400 text-sm uppercase tracking-wider">Time Casa</Label>
            <Input
              id="team_home"
              name="team_home"
              value={formData.team_home}
              onChange={handleInputChange}
              placeholder="Ex: Flamengo"
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-team-home"
            />
          </div>
          <div>
            <Label htmlFor="team_away" className="text-zinc-400 text-sm uppercase tracking-wider">Time Visitante</Label>
            <Input
              id="team_away"
              name="team_away"
              value={formData.team_away}
              onChange={handleInputChange}
              placeholder="Ex: Botafogo"
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-team-away"
            />
          </div>
          <div>
            <Label htmlFor="league" className="text-zinc-400 text-sm uppercase tracking-wider">Campeonato</Label>
            <Input
              id="league"
              name="league"
              value={formData.league}
              onChange={handleInputChange}
              placeholder="Ex: Campeonato Carioca"
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-league"
            />
          </div>
          <div>
            <Label htmlFor="stadium" className="text-zinc-400 text-sm uppercase tracking-wider">Estádio</Label>
            <Input
              id="stadium"
              name="stadium"
              value={formData.stadium}
              onChange={handleInputChange}
              placeholder="Ex: Maracanã"
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-stadium"
            />
          </div>
          <div>
            <Label htmlFor="date" className="text-zinc-400 text-sm uppercase tracking-wider">Data</Label>
            <Input
              id="date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-date"
            />
          </div>
          <div>
            <Label htmlFor="time" className="text-zinc-400 text-sm uppercase tracking-wider">Horário</Label>
            <Input
              id="time"
              name="time"
              type="time"
              value={formData.time}
              onChange={handleInputChange}
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-time"
            />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <Label htmlFor="channels" className="text-zinc-400 text-sm uppercase tracking-wider">Canais (separados por vírgula)</Label>
            <Input
              id="channels"
              name="channels"
              value={formData.channels}
              onChange={handleInputChange}
              placeholder="Ex: TV Globo, SporTV, Premiere"
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
              data-testid="input-channels"
            />
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <Label htmlFor="home_lineup" className="text-zinc-400 text-sm uppercase tracking-wider">Escalação Casa</Label>
            <Textarea
              id="home_lineup"
              name="home_lineup"
              value={formData.home_lineup}
              onChange={handleInputChange}
              placeholder="Ex: Goleiro; Zagueiro, ..."
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1 min-h-[80px]"
              data-testid="input-home-lineup"
            />
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <Label htmlFor="away_lineup" className="text-zinc-400 text-sm uppercase tracking-wider">Escalação Visitante</Label>
            <Textarea
              id="away_lineup"
              name="away_lineup"
              value={formData.away_lineup}
              onChange={handleInputChange}
              placeholder="Ex: Goleiro; Zagueiro, ..."
              className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1 min-h-[80px]"
              data-testid="input-away-lineup"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="home_score" className="text-zinc-400 text-sm uppercase tracking-wider">Placar Casa</Label>
              <Input
                id="home_score"
                name="home_score"
                type="number"
                min="0"
                value={formData.home_score}
                onChange={handleInputChange}
                placeholder="0"
                className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
                data-testid="input-home-score"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="away_score" className="text-zinc-400 text-sm uppercase tracking-wider">Placar Visitante</Label>
              <Input
                id="away_score"
                name="away_score"
                type="number"
                min="0"
                value={formData.away_score}
                onChange={handleInputChange}
                placeholder="0"
                className="bg-pitch-black border-zinc-700 focus:border-neon-green mt-1"
                data-testid="input-away-score"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="space-y-4">
        <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Templates Disponíveis</h2>
        
        {templates.map((template) => {
          const isExpanded = expandedTemplate === template.id;
          const generatedContent = generateContent(template);
          
          return (
            <div key={template.id} className="bg-card-dark border border-zinc-800 overflow-hidden">
              {/* Template Header */}
              <button
                onClick={() => setExpandedTemplate(isExpanded ? null : template.id)}
                className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-elevated/50 transition-colors"
                data-testid={`template-header-${template.id}`}
              >
                <div className="text-left">
                  <h3 className="font-heading text-lg font-bold uppercase">{template.name}</h3>
                  <p className="text-sm text-zinc-500">{template.description}</p>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </button>

              {/* Template Content */}
              {isExpanded && (
                <div className="px-4 md:px-6 pb-4 md:pb-6 border-t border-zinc-800">
                  <div className="flex justify-end mt-4 mb-3">
                    <Button
                      onClick={() => copyToClipboard(template.id, generatedContent)}
                      className="bg-neon-green text-black font-bold uppercase tracking-wider hover:bg-white gap-2"
                      data-testid={`copy-template-${template.id}`}
                    >
                      {copiedId === template.id ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copiar Texto
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="bg-pitch-black p-4 text-sm text-zinc-300 whitespace-pre-wrap font-mono border border-zinc-800 overflow-x-auto max-h-[400px] overflow-y-auto">
                    {generatedContent}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TemplatesPage;
